import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Callout } from "../components/ui/Callout";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Pagination } from "../components/ui/Pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "../components/ui/Table";
import { api } from "../lib/api";
import type { Food, StatsResponse } from "../lib/types";
import { fmtNumber, fmtPercent } from "../lib/format";

const PAGE_SIZE = 25;

// Tremor Raw Badge supports: default | neutral | success | error | warning.
// Map our food sources onto those five tones — no exact 1:1, just consistent.
const SOURCE_VARIANT: Record<
  string,
  "default" | "neutral" | "success" | "error" | "warning"
> = {
  usda: "default",
  mealdb: "warning",
  wikidata: "neutral",
  wikibooks: "neutral",
  openrouter: "success",
  claude: "success",
  user: "default",
  manual: "neutral",
};

type FoodsPage = {
  items: Food[];
  total: number;
  page: number;
  pageSize: number;
};

export function FoodsPage() {
  const [raw, setRaw] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  // Active source filter (clicked row in the source-breakdown card).
  // null = no filter. Clicking the same source again toggles it off.
  const [source, setSource] = useState<string | null>(null);

  // 250ms debounce — prevents firing a fetch per keystroke. Empty input is a
  // valid distinct query: the admin endpoint returns the most recent rows
  // when q is omitted.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(raw.trim()), 250);
    return () => clearTimeout(t);
  }, [raw]);

  // Reset to page 1 when the search term OR source filter changes —
  // otherwise a paginated result for one query silently bleeds into the
  // next on the same page index.
  useEffect(() => {
    setPage(1);
  }, [debounced, source]);

  const path = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });
    if (debounced) params.set("q", debounced);
    if (source) params.set("source", source);
    return `/v1/admin/foods?${params.toString()}`;
  }, [debounced, source, page]);

  const { data, isLoading, error, isFetching } = useQuery<FoodsPage>({
    queryKey: ["admin-foods", path],
    queryFn: () => api<FoodsPage>(path),
    placeholderData: (prev) => prev,
  });

  // Reuse the existing /v1/admin/stats endpoint for the top strip. Same
  // query key Insights uses → if the user navigated from /#/stats the
  // strip renders instantly from cache. Independent of the page-paged
  // foods query above.
  const { data: stats } = useQuery<StatsResponse>({
    queryKey: ["stats", 30],
    queryFn: () => api<StatsResponse>("/v1/admin/stats?days=30"),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const showingFrom =
    data && data.items.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const showingTo =
    data && data.items.length > 0 ? showingFrom + data.items.length - 1 : 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
          Foods
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
          Type a prefix to search. Empty shows the most recently updated rows.
        </p>
      </header>

      <StatsStrip total={data?.total ?? null} stats={stats ?? null} />

      {stats && stats.bySource.length > 0 && (
        <SourceBreakdown
          stats={stats}
          active={source}
          onToggle={(s) => setSource((cur) => (cur === s ? null : s))}
        />
      )}

      {stats && <FoodLookupPanel stats={stats} />}

      <div className="flex max-w-2xl items-center gap-3">
        <div className="max-w-md flex-1">
          <Input
            type="search"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="e.g. pad"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <CanonicalizeButton />
      </div>

      {error && (
        <Callout title="Failed to load" variant="error">
          {(error as Error).message}
        </Callout>
      )}

      {isLoading && !data ? (
        <Skeleton />
      ) : !data || data.items.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-gray-500 dark:text-gray-500">
            {debounced ? "No foods match." : "No foods yet."}
          </p>
        </Card>
      ) : (
        <>
          {/* Override TableRoot's default whitespace-nowrap so the Name cell
              wraps and the source/searches/actions stay visible without
              horizontal scroll. */}
          <TableRoot className="!whitespace-normal">
            <Table>
              <TableHead>
                <TableRow>
                  {/* Compact layout: Name+meta / Source / Searches / Actions.
                        Region, last_edited_by, and user_id were folded into
                        the meta line under each name so the source badge
                        stays on screen at typical desktop widths without
                        horizontal scroll. */}
                  <TableHeaderCell className="!px-3 !py-2">
                    Name
                  </TableHeaderCell>
                  <TableHeaderCell className="!px-3 !py-2">
                    Source
                  </TableHeaderCell>
                  <TableHeaderCell className="!px-3 !py-2 text-right">
                    Searches
                  </TableHeaderCell>
                  <TableHeaderCell className="!px-3 !py-2 text-right">
                    Actions
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((f) => (
                  <FoodRow key={f.id} food={f} />
                ))}
              </TableBody>
            </Table>
          </TableRoot>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={data.total}
            from={showingFrom}
            to={showingTo}
            busy={isFetching}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}

/**
 * Top-of-page stats strip. Three KPIs the Foods page wants at a glance:
 *   - Total foods in the catalog (live, from the paged endpoint's `total`)
 *   - Dominant adapter source + its share (from /v1/admin/stats bySource)
 *   - Cache hit rate (from /v1/admin/stats cacheEffectiveness)
 *
 * Renders as a single borderless tinted card, matching the KPI strip on
 * Insights. Lifts each value as `—` while stats are still loading so the
 * layout doesn't reflow.
 */
function StatsStrip({
  total,
  stats,
}: {
  total: number | null;
  stats: StatsResponse | null;
}) {
  const topSource = stats?.bySource?.[0] ?? null;
  const sourceShare =
    topSource && stats && stats.summary.totalFoods > 0
      ? topSource.count / stats.summary.totalFoods
      : null;
  const hitRate = stats?.cacheEffectiveness?.hitRate ?? null;

  return (
    <Card>
      <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-4">
        <FoodKpi
          label="Total foods"
          value={total != null ? fmtNumber(total) : "—"}
        />
        <FoodKpi
          label="Top source"
          value={topSource ? topSource.source : "—"}
          sub={
            sourceShare != null
              ? `${fmtPercent(sourceShare)} of catalog`
              : undefined
          }
        />
        <FoodKpi
          label="Cache hit rate"
          value={hitRate != null ? fmtPercent(hitRate) : "—"}
          sub={
            stats
              ? `${fmtNumber(stats.cacheEffectiveness.cacheHits)} of ${fmtNumber(stats.cacheEffectiveness.totalSearches)} searches`
              : undefined
          }
        />
        <FoodKpi
          label="Lookup fail rate"
          value={
            stats?.foodLookupHealth.fail_rate != null
              ? fmtPercent(stats.foodLookupHealth.fail_rate)
              : "—"
          }
          sub={
            stats
              ? `${fmtNumber(stats.foodLookupHealth.missed)} misses of ${fmtNumber(stats.foodLookupHealth.total)} lookups`
              : undefined
          }
        />
      </div>
    </Card>
  );
}

function FoodKpi({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div>
      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <p className="mt-2 text-2xl font-semibold tabular-nums leading-none text-gray-950 dark:text-gray-50">
        {value}
      </p>
      {sub && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{sub}</p>
      )}
    </div>
  );
}

/**
 * Source breakdown card. Renders one click-to-filter row per source the
 * admin /v1/admin/stats endpoint returned, sorted descending by count.
 *
 * The active source (if any) is highlighted with a darker tint; clicking
 * it again toggles the filter off. Clicking a different source replaces
 * the active filter — single-select, since the table can only filter on
 * one source at a time today.
 *
 * Visual language matches Insights's MonoBarList: monochrome stone overlay
 * proportional to the count, label on the left, count on the right. The
 * row also doubles as the click target for the filter toggle.
 */
function SourceBreakdown({
  stats,
  active,
  onToggle,
}: {
  stats: StatsResponse;
  active: string | null;
  onToggle: (source: string) => void;
}) {
  const items = stats.bySource;
  const max = items.reduce((m, b) => (b.count > m ? b.count : m), 0);
  const totalFoods = stats.summary.totalFoods;

  return (
    <Card>
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
          Source breakdown
        </h2>
        {active && (
          <button
            type="button"
            onClick={() => onToggle(active)}
            className="text-xs text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline dark:text-gray-400 dark:hover:text-gray-50"
          >
            Clear filter
          </button>
        )}
      </div>
      <ul className="space-y-1.5">
        {items.map((b) => {
          const pct =
            max > 0 ? Math.max(2, Math.round((b.count / max) * 100)) : 0;
          const share = totalFoods > 0 ? b.count / totalFoods : 0;
          const isActive = active === b.source;
          return (
            <li key={b.source} className="relative">
              <button
                type="button"
                onClick={() => onToggle(b.source)}
                className={`relative block w-full cursor-pointer text-left transition-colors ${
                  isActive
                    ? "ring-1 ring-inset ring-gray-300 dark:ring-white/20"
                    : "hover:ring-1 hover:ring-inset hover:ring-gray-200 dark:hover:ring-white/10"
                } rounded-md`}
                aria-pressed={isActive}
              >
                <span
                  aria-hidden
                  className={`absolute inset-y-0 left-0 rounded-md ${
                    isActive
                      ? "bg-stone-300 dark:bg-white/[0.14]"
                      : "bg-stone-200 dark:bg-white/[0.08]"
                  }`}
                  style={{ width: `${pct}%` }}
                />
                <div className="relative flex items-center justify-between gap-3 px-2 py-1.5 text-sm">
                  <span className="truncate text-gray-900 dark:text-gray-50">
                    {b.source}
                  </span>
                  <span className="flex shrink-0 items-baseline gap-2 tabular-nums text-gray-500 dark:text-gray-400">
                    <span>{fmtPercent(share)}</span>
                    <span className="text-gray-400 dark:text-gray-500">
                      {fmtNumber(b.count)}
                    </span>
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function FoodLookupPanel({ stats }: { stats: StatsResponse }) {
  const health = stats.foodLookupHealth;
  const sourceMax = health.by_source.reduce(
    (max, row) => Math.max(max, row.count),
    0,
  );
  const reasonMax = health.miss_reasons.reduce(
    (max, row) => Math.max(max, row.count),
    0,
  );

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <Card>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
            Lookup source
          </h2>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            last {health.range.days}d
          </span>
        </div>
        {health.by_source.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-500">
            No lookup source data yet.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {health.by_source.map((row) => (
              <MeterRow
                key={row.source}
                label={row.source}
                value={row.count}
                max={sourceMax}
              />
            ))}
          </ul>
        )}
      </Card>
      <Card>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
            Lookup misses
          </h2>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {health.fail_rate != null ? fmtPercent(health.fail_rate) : "—"}
          </span>
        </div>
        {health.miss_reasons.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-500">
            No missed lookups in this window.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {health.miss_reasons.map((row) => (
              <MeterRow
                key={row.reason}
                label={row.reason}
                value={row.count}
                max={reasonMax}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function MeterRow({
  label,
  value,
  max,
}: {
  label: string;
  value: number;
  max: number;
}) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <li className="relative">
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 rounded-sm bg-stone-200 dark:bg-white/10"
        style={{ width: `${pct}%` }}
      />
      <div className="relative flex items-center justify-between gap-3 px-2 py-1.5 text-sm">
        <span className="truncate text-gray-900 dark:text-gray-50">
          {label}
        </span>
        <span className="shrink-0 tabular-nums text-gray-500 dark:text-gray-400">
          {fmtNumber(value)}
        </span>
      </div>
    </li>
  );
}

function FoodRow({ food }: { food: Food }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return <EditingRow food={food} onClose={() => setEditing(false)} />;
  }
  return <DisplayRow food={food} onEdit={() => setEditing(true)} />;
}

function DisplayRow({ food, onEdit }: { food: Food; onEdit: () => void }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () =>
      api<{ id: string; deleted_at: number }>(`/v1/admin/foods/${food.id}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-foods"] }),
  });

  // Render comma-joined ingredient list under the name. Truncated visually but
  // selectable so the admin can still copy the full string.
  const ingredients = food.ingredients ?? [];
  const ingredientText = ingredients.length > 0 ? ingredients.join(", ") : null;

  // Build a small meta line with the now-hidden columns (region · last edit
  // · user). Skips parts that are empty so we don't render lonely separators.
  const metaParts: string[] = [];
  if (food.region) metaParts.push(food.region);
  if (food.last_edited_by) metaParts.push(`edited by ${food.last_edited_by}`);
  if (food.user_id) metaParts.push(`uid ${food.user_id.slice(0, 8)}`);
  const metaLine = metaParts.length > 0 ? metaParts.join(" · ") : null;

  return (
    <TableRow className="align-top">
      <TableCell className="!px-3 !py-2 font-medium whitespace-normal text-gray-900 dark:text-gray-50">
        <div>{food.name}</div>
        {ingredientText && (
          <div
            className="mt-0.5 line-clamp-2 text-xs font-normal text-gray-500 dark:text-gray-500"
            title={ingredientText}
          >
            {ingredientText}
          </div>
        )}
        {metaLine && (
          <div
            className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-600"
            title={food.user_id ?? undefined}
          >
            {metaLine}
          </div>
        )}
      </TableCell>
      <TableCell className="!px-3 !py-2">
        <Badge variant={SOURCE_VARIANT[food.source] ?? "neutral"}>
          {food.source}
        </Badge>
      </TableCell>
      <TableCell className="!px-3 !py-2 text-right tabular-nums">
        {fmtNumber(food.search_count)}
      </TableCell>
      <TableCell className="!px-3 !py-2 text-right whitespace-nowrap">
        <div className="flex justify-end gap-1">
          <Button
            variant="secondary"
            className="!px-2 !py-1 !text-xs"
            onClick={onEdit}
            disabled={remove.isPending}
          >
            Edit
          </Button>
          <Button
            variant="destructive"
            className="!px-2 !py-1 !text-xs"
            onClick={() => {
              // Plain confirm — soft-delete is reversible at the DB layer
              // (deleted_at flag) but there's no admin "undo" UI yet, so a
              // tactile confirm is worth the friction.
              if (
                window.confirm(
                  `Delete "${food.name}"? iOS clients will pick up the removal on next sync.`,
                )
              ) {
                remove.mutate();
              }
            }}
            disabled={remove.isPending}
          >
            {remove.isPending ? "…" : "Remove"}
          </Button>
        </div>
        {remove.isError && (
          <div className="mt-1 text-xs text-red-500">
            {(remove.error as Error).message}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

function EditingRow({ food, onClose }: { food: Food; onClose: () => void }) {
  const qc = useQueryClient();
  const initialIngredients = (food.ingredients ?? []).join(", ");
  const [name, setName] = useState(food.name);
  const [ingredients, setIngredients] = useState(initialIngredients);

  const save = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const list = ingredients
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const body: Record<string, unknown> = {};
      if (trimmedName !== food.name) body.name = trimmedName;
      if (initialIngredients !== ingredients) body.ingredients = list;
      if (Object.keys(body).length === 0) return food;
      return api<Food>(`/v1/admin/foods/${food.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-foods"] });
      onClose();
    },
  });

  return (
    <TableRow className="align-top">
      <TableCell colSpan={4}>
        <div className="space-y-2 py-2">
          <div className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-xs text-gray-500">Name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex items-start gap-2">
            <span className="w-24 shrink-0 pt-2 text-xs text-gray-500">
              Ingredients
            </span>
            <textarea
              value={ingredients}
              onChange={(e) => setIngredients(e.target.value)}
              placeholder="comma-separated, e.g. milk, sugar, vanilla"
              rows={3}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-50"
            />
          </div>
          {save.isError && (
            <Callout title="Save failed" variant="error">
              {(save.error as Error).message}
            </Callout>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="secondary"
              className="!text-xs"
              onClick={onClose}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="!text-xs"
              onClick={() => save.mutate()}
              disabled={save.isPending || name.trim().length === 0}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * One-shot fixup: rewrites every existing food + ingredient row's display
 * casing to match what new rows produce. Stays on the Foods page (rather than
 * Settings) because the result you want to verify is right here in the table.
 */
function CanonicalizeButton() {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: () =>
      api<{
        foods_scanned: number;
        foods_updated: number;
        ingredients_scanned: number;
        ingredients_updated: number;
      }>(`/v1/admin/maintenance/canonicalize`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-foods"] }),
  });

  const result = run.data;
  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="secondary"
        className="!text-xs"
        onClick={() => {
          if (
            window.confirm(
              "Recanonicalize ALL foods + ingredients display casing? This is a one-shot fixup and is safe to re-run.",
            )
          ) {
            run.mutate();
          }
        }}
        disabled={run.isPending}
      >
        {run.isPending ? "Fixing…" : "Fix casing"}
      </Button>
      {result && (
        <span className="text-xs text-gray-500 dark:text-gray-500">
          Updated {result.foods_updated}/{result.foods_scanned} foods,{" "}
          {result.ingredients_updated}/{result.ingredients_scanned} ingredients.
        </span>
      )}
      {run.isError && (
        <span className="text-xs text-red-500">
          {(run.error as Error).message}
        </span>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-10 w-full animate-pulse rounded-md bg-gray-100 dark:bg-gray-900"
        />
      ))}
    </div>
  );
}
